import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useListFeedback, getListFeedbackQueryKey, type Feedback } from '@edison/api-client-react';
import { SlidePanel } from '@/components/ui/slide-panel.js';

interface StatusStyle { border: string; text: string; label: string; opacity?: string }

const STATUS_CONFIG: Record<string, StatusStyle | undefined> = {
  open:      { border: 'border-l-red-500',    text: 'text-red-400',    label: 'Open' },
  in_review: { border: 'border-l-amber-500',  text: 'text-amber-400',  label: 'In Review' },
  planned:   { border: 'border-l-indigo-500', text: 'text-indigo-400', label: 'Planned' },
  resolved:  { border: 'border-l-green-600',  text: 'text-green-500',  label: 'Resolved', opacity: 'opacity-60' },
  closed:    { border: 'border-l-green-600',  text: 'text-green-500',  label: 'Resolved', opacity: 'opacity-60' },
};

const DEFAULT_STATUS: StatusStyle = { border: 'border-l-red-500', text: 'text-red-400', label: 'Open' };

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function syncFooterText(lastPolledAt: string | null | undefined): string {
  if (!lastPolledAt) return 'Not yet synced';
  return `Status synced ${relativeTime(lastPolledAt)}`;
}

function isSyncStale(lastPolledAt: string | null | undefined): boolean {
  if (!lastPolledAt) return false;
  const diff = Date.now() - new Date(lastPolledAt).getTime();
  return diff > 15 * 60_000;
}

function typeLabel(type: string | undefined): string {
  if (type === 'feature_request') return 'Feature Request';
  if (type === 'bug_report') return 'Bug Report';
  return type ?? '';
}

function TicketCard({ ticket, onClick }: { ticket: Feedback; onClick: () => void }) {
  const status = ticket.status ?? 'open';
  const cfg = STATUS_CONFIG[status] ?? DEFAULT_STATUS;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-md border border-white/8 border-l-2 ${cfg.border} bg-white/3 hover:bg-white/6 transition-colors p-3 ${cfg.opacity ?? ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-primary-foreground leading-snug line-clamp-2 flex-1">
          {ticket.title}
        </p>
        <span className={`text-xs font-medium shrink-0 mt-0.5 ${cfg.text}`}>
          {cfg.label}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs text-white/30">{typeLabel(ticket.type)}</span>
        {ticket.createdAt && (
          <span className="text-xs text-white/20">· {relativeTime(ticket.createdAt)}</span>
        )}
      </div>
    </button>
  );
}

function TicketDetail({ ticket }: { ticket: Feedback }) {
  const status = ticket.status ?? 'open';
  const cfg = STATUS_CONFIG[status] ?? DEFAULT_STATUS;

  return (
    <div className="p-5 space-y-4">
      <div className="space-y-1">
        <p className="text-xs text-white/40 uppercase tracking-widest">Type</p>
        <p className="text-sm text-primary-foreground">{typeLabel(ticket.type)}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs text-white/40 uppercase tracking-widest">Status</p>
        <span className={`text-sm font-medium ${cfg.text}`}>{cfg.label}</span>
      </div>
      {ticket.description && (
        <div className="space-y-1">
          <p className="text-xs text-white/40 uppercase tracking-widest">Description</p>
          <p className="text-sm text-primary-foreground/80 whitespace-pre-wrap leading-relaxed">
            {ticket.description}
          </p>
        </div>
      )}
      {ticket.priority && (
        <div className="space-y-1">
          <p className="text-xs text-white/40 uppercase tracking-widest">Priority</p>
          <p className="text-sm text-primary-foreground capitalize">{ticket.priority}</p>
        </div>
      )}
      {ticket.createdAt && (
        <div className="space-y-1">
          <p className="text-xs text-white/40 uppercase tracking-widest">Submitted</p>
          <p className="text-sm text-primary-foreground/60">{relativeTime(ticket.createdAt)}</p>
        </div>
      )}
    </div>
  );
}

export function MyTicketsColumn() {
  const [selectedTicket, setSelectedTicket] = useState<Feedback | null>(null);

  const { data, isLoading, error } = useListFeedback(undefined, {
    query: {
      queryKey: getListFeedbackQueryKey(undefined),
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    },
  });

  const items = data?.data?.items ?? [];
  const lastPolledAt = data?.data?.lastPolledAt;
  const stale = isSyncStale(lastPolledAt);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {isLoading && (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 rounded-md skeleton" />
            ))}
          </>
        )}

        {!isLoading && !!error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Failed to load tickets.</span>
          </div>
        )}

        {!isLoading && !error && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-sm text-white/40">No tickets submitted yet.</p>
            <p className="text-xs text-white/25 mt-1">
              Use the form to report a bug or request a feature.
            </p>
          </div>
        )}

        {!isLoading && !error && items.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            onClick={() => setSelectedTicket(ticket)}
          />
        ))}
      </div>

      {/* Sync footer */}
      {!isLoading && !error && (
        <div className={`px-3 py-2 border-t border-white/8 shrink-0 text-xs ${stale ? 'text-amber-400' : 'text-white/25'}`}>
          {syncFooterText(lastPolledAt)}
        </div>
      )}

      {/* Ticket detail slide panel */}
      <SlidePanel
        open={selectedTicket !== null}
        onClose={() => setSelectedTicket(null)}
        title={selectedTicket?.title ?? 'Ticket'}
        className="!bg-[oklch(0.22_0.05_250)] border-white/10 [&>div:first-child]:border-white/8 [&_h2]:text-white [&_button]:text-white/40 [&_button:hover]:text-white [&_button:hover]:bg-white/8"
      >
        {selectedTicket && <TicketDetail ticket={selectedTicket} />}
      </SlidePanel>
    </div>
  );
}
